//------------------------------------------------------------------------------
// name: BranchBankSelector.tsx
// author: mudas
// created: 2025/12/09
//------------------------------------------------------------------------------

import React, { FC, useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useDebounce } from 'ahooks';

import { useProxyValue } from 'common/components';

import { Identity } from 'common/types';
import { SimpleValueType, ProxyValue } from 'common/components';
import service from 'common/service';

import { Select, SelectProps } from '@fuxi/eevee-ui';

type ValueType = SimpleValueType;
type OptionType = Identity;

export interface IProps extends Omit<SelectProps<ValueType, OptionType>, 'onChange' | 'value'> {
  bankId?: string;
  branchBankName?: string;

  // 是否返回 option 替代 value
  replaceAsOption?: boolean;

  // 受 replaceAsOption 影响，value 为 option 时，onChange 回调参数为 option，否则为 value
  value?: ProxyValue<OptionType>;
  onChange?: (value: ProxyValue<OptionType>) => void;
}

const BranchBankSelector: FC<IProps> = (props) => {
  const { bankId, branchBankName, replaceAsOption, value: baseValue, onChange: baseOnChange, ...otherProps } = props;

  const [bankName, setBankName] = useState<string>(branchBankName || '');

  const { selectValue, onChange } = useProxyValue<OptionType>({
    fieldName: 'bankNo',
    replaceAsOption,
    value: baseValue,
    onChange: baseOnChange,
  });

  // 原 useSafeQuery 的 debounceWait 换成对输入值本身防抖：bankName 会进 queryKey，
  // 不防抖的话搜索框每敲一个字都会发一次请求
  const debouncedBankName = useDebounce(bankName, { wait: 300, leading: true });

  const { data, isFetching } = useQuery({
    queryKey: ['getSubBankList', bankId, debouncedBankName],
    queryFn: async () => {
      const res = await service.userSetting.getSubBankList({
        bankId: bankId!,
        subBankName: debouncedBankName || undefined,
      });
      return res.data;
    },
    enabled: !!bankId,
    cacheTime: 10 * 60 * 1000,
    staleTime: 10 * 60 * 1000,
  });

  useEffect(() => {
    branchBankName !== undefined && setBankName(branchBankName);
  }, [branchBankName]);

  return (
    <Select<ValueType, OptionType>
      loading={isFetching}
      options={data?.items}
      value={selectValue}
      showSearch
      allowClear
      placeholder="请选择银行支行"
      fieldNames={{ label: 'bankName', value: 'bankNo' }}
      filterOption={false}
      onSearch={(value: string) => setBankName(value)}
      onChange={onChange}
      {...otherProps}
    />
  );
};

export default BranchBankSelector;
